import { Box, Text, Icon } from '@nimbus-ds/components';
import { ChevronLeftIcon } from '@nimbus-ds/icons';
import { Menu } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import {
  useMenuScrollShadow,
  useSectionActive,
} from 'App/components/Menu/hooks';
import ShowByRole from 'App/components/ShowByRole';
import { useNavegate, useHandleMenuClick, useIsInAppBrowser } from 'App/hooks';
import { useResponsive } from 'commons/components';
import { ACCOUNT_ROUTES } from 'domains/Account/accountRoutes';
import { trackingSessionManagementMenuItemClick } from 'domains/Account/tracking';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import {
  useGetUserInfo,
  useHeaderMenuData,
  useIsMobileDevice,
} from 'domains/Auth/hooks';
import useLogout from 'domains/Auth/pages/LogoutPage/useLogout';
import { BILLING_ROUTES } from 'domains/Billing/billingRoutes';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import { INVOICES_ROUTES } from 'domains/Billing/Invoices';
import { TRANSACTION_FEES_ROUTES } from 'domains/Billing/TransactionFees/transactionFeesRoutes';
import { trackingEditUserClick } from 'domains/Configurations/tracking';
import { PATH } from '../../../utils';
import './NewAccountMenuContent.scss';

interface AccountMenuContentProps {
  device?: 'mobile' | 'desktop';
}

function NewAccountMenuContent({ device }: Readonly<AccountMenuContentProps>) {
  const { t } = useTranslation('common');
  const { pathname } = useNavegate();
  const isMobile = device === 'mobile';
  const isMobileDevice = useIsMobileDevice();
  const isInAppBrowserOpen = useIsInAppBrowser();
  const { isMobile: isMobileResponsive } = useResponsive();
  const { handleClickItem } = useHandleMenuClick();
  const logout = useLogout();
  const { goTo } = useNavegate();
  const { name } = useHeaderMenuData();
  const { email, id, isEmployee } = useGetUserInfo();
  const { validateRoles } = useRoles();
  const hasTopMenuRole = validateRoles(['payments_and_subscriptions', 'full']);
  const isWebVersion = !isMobileDevice && !isInAppBrowserOpen;

  const handleSessionManagementMenuItemClick = () => {
    trackingSessionManagementMenuItemClick();
    handleClickItem();
  };
  const handleEditAccountClick = () => {
    trackingEditUserClick('account');
    handleClickItem();
  };

  const handleClickLogOut = isMobile ? handleClickItem : logout;
  const menuBodyRef = useMenuScrollShadow();
  const isSectionActive = useSectionActive();

  const profielPath = generatePath(ACCOUNT_ROUTES.userForm, { id });

  return (
    <div className="stratus--user-menu-content">
      {isMobileResponsive && (
        <Menu.Header>
          <Box display="flex" flexDirection="column" gap="4">
            <Box
              display="flex"
              gap="1"
              as="button"
              backgroundColor="transparent"
              borderColor="transparent"
              onClick={() => goTo('/')}
              alignItems="center"
              cursor="pointer"
            >
              <Icon
                color="neutral-textHigh"
                source={<ChevronLeftIcon size={14} />}
              />
              <Text fontWeight="medium" lineClamp={1} color="neutral-textHigh">
                {name}
              </Text>
            </Box>
          </Box>
        </Menu.Header>
      )}
      <div
        className={`stratus--menu-body ${
          !isMobileResponsive && hasTopMenuRole
            ? 'desktop-padding'
            : 'non-top-menu'
        }`}
        ref={menuBodyRef}
      >
        {hasTopMenuRole && (
          <Menu.Body>
            <Box display="flex" gap="1-5" flexDirection="column">
              {isWebVersion && (
                <ShowByRole includeRoles={['payments_and_subscriptions']}>
                  <Menu.Button
                    as={Link}
                    to={CHECKOUT_ROUTES.plansAndSubscriptions}
                    active={
                      isSectionActive(CHECKOUT_ROUTES.plansAndSubscriptions) ||
                      isSectionActive(BILLING_ROUTES.coupons) ||
                      isSectionActive(INVOICES_ROUTES.invoicesList) ||
                      isSectionActive(
                        TRANSACTION_FEES_ROUTES.transactionFees,
                      ) ||
                      isSectionActive(BILLING_ROUTES.plans)
                    }
                    label={t(
                      'accountMenu.accountDetails.plansAndSubscriptions',
                    )}
                  />
                </ShowByRole>
              )}

              <ShowByRole includeRoles={['full']}>
                <Menu.Button
                  as={Link}
                  to={ACCOUNT_ROUTES.businessData}
                  active={isSectionActive(ACCOUNT_ROUTES.businessData)}
                  label={t(
                    'accountMenu.accountDetails.dataBusinessAndCancellation',
                  )}
                />
              </ShowByRole>
            </Box>
          </Menu.Body>
        )}
        <div className="stratus--menu-body-gray">
          <Menu.Body>
            <Menu.Section>
              <Box marginLeft="2">
                <Text fontSize="caption" lineClamp={1} wordBreak="break-all">
                  {email}
                </Text>
              </Box>
            </Menu.Section>
            <Menu.Section>
              {!isEmployee && (
                <Menu.Button
                  as={Link}
                  onClick={handleEditAccountClick}
                  to={profielPath}
                  active={pathname.startsWith(profielPath)}
                  label={t('accountMenu.editProfile')}
                />
              )}
              <Menu.Button
                as={Link}
                to={PATH.authenticationFactor}
                active={pathname.startsWith(PATH.authenticationFactor)}
                label={t('accountMenu.security.authenticationFactor')}
                onClick={handleClickItem}
              />
              <Menu.Button
                as={Link}
                to={PATH.sessionManagement}
                active={pathname.startsWith(PATH.sessionManagement)}
                label={t('accountMenu.security.sessionManagement')}
                onClick={handleSessionManagementMenuItemClick}
              />
            </Menu.Section>
            {isMobile ? (
              <Menu.Button
                as={Link}
                to={PATH.logout}
                label={t('menu.closeSession')}
                onClick={handleClickLogOut}
              />
            ) : (
              <Menu.Button
                label={t('menu.closeSession')}
                onClick={handleClickLogOut}
              />
            )}
          </Menu.Body>
        </div>
      </div>
    </div>
  );
}

export default NewAccountMenuContent;
